import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { IMAGES_IN_PLAN } from '@/constants/common';

import { PLAN_PERKS } from './content';
import {
  CREDITS_PER_DESIGN,
  designsFor,
  useInteriorLandingT,
  useStartHref,
} from './kit';
import { PAID_PLANS, type PlanPrices } from './pages';

const formatCount = (value: number, lang: string) =>
  new Intl.NumberFormat(`${lang}-u-nu-latn`).format(value);

const formatPrice = (value: number, lang: string) =>
  new Intl.NumberFormat(`${lang}-u-nu-latn`, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

/**
 * What each plan buys, in designs. Numbers come from the plan limits, the
 * credit price of a design and the plans' prices, so they follow any change
 * there.
 */
export default function Plans({ prices }: { prices: PlanPrices }) {
  const { t, lang } = useInteriorLandingT();
  const reveal = useReveal<HTMLDivElement>(0.2);
  const startHref = useStartHref();

  const stops = [
    { plan: 'Free', count: 1, note: t('free design every month'), price: '' },
    ...PAID_PLANS.map((plan) => ({
      plan,
      count: designsFor(IMAGES_IN_PLAN[plan]),
      note: t('designs a month'),
      price: t('{{price}} a month', { price: formatPrice(prices[plan], lang) }),
    })),
  ];

  return (
    <section id='plans' className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-5 sm:px-6 lg:px-8'>
        <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
          <div className='lg:col-span-7'>
            <SectionHeading
              size='md'
              title={t('Start free,')}
              tail={t('upgrade for more designs.')}
              lead={t(
                'Every account can make its first design free. Paid plans add more designs every month, plus:',
              )}
            />
            <ul className='mt-6 grid gap-x-6 gap-y-2.5 sm:grid-cols-2'>
              {PLAN_PERKS.map((perk) => (
                <li
                  key={perk}
                  className={clsxm(
                    'flex items-center gap-2.5 text-[15px] font-medium',
                    tone.ink,
                  )}
                >
                  <span className='h-1.5 w-1.5 flex-none rounded-full bg-primary-500' />
                  {t(perk)}
                </li>
              ))}
            </ul>
          </div>
          <div className='flex flex-wrap gap-3 lg:col-span-5 lg:justify-end'>
            <Link href={startHref} className={buttonPrimary}>
              {t('Start designing')}
              <ArrowGlyph />
            </Link>
            <Link href='/pricing' className={buttonSecondary}>
              {t('Compare plans')}
            </Link>
          </div>
        </div>

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-[#0f0f28]/[0.1] pt-10 lg:grid-cols-4 dark:border-white/[0.1]',
          )}
        >
          {stops.map((stop, index) => (
            <div
              key={stop.plan}
              className={styles.rv}
              style={delay(index * 90)}
            >
              <p className={clsxm('text-sm font-semibold', tone.quiet)}>
                {t(stop.plan)}
              </p>
              <p
                className={clsxm(
                  styles.display,
                  'mt-3 text-[2.75rem] font-semibold leading-none tabular-nums sm:text-[3.25rem]',
                  index === 0
                    ? 'text-primary-600 dark:text-primary-400'
                    : tone.ink,
                )}
              >
                {formatCount(stop.count, lang)}
              </p>
              <p className={clsxm('mt-3 text-[15px]', tone.body)}>
                {stop.note}
              </p>
              <p
                className={clsxm(
                  'mt-1 text-sm font-semibold tabular-nums',
                  stop.price
                    ? tone.ink
                    : 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                {stop.price || t('No credit card needed')}
              </p>
            </div>
          ))}
        </div>

        <p className={clsxm('mt-10 text-[13px]', tone.quiet)}>
          {t(
            'A design at the standard size uses {{credits}} credits. 4K designs and 360° views use more.',
            { credits: CREDITS_PER_DESIGN },
          )}
        </p>
      </div>
    </section>
  );
}
